<template>
  <div class="client-offers">
    <section class="page-header">
      <div class="container">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <h1 class="page-title">
              <i class="fas fa-briefcase me-2"></i>
              Mes offres d'emploi
            </h1>
            <p class="page-subtitle mb-0">
              Retrouvez ici toutes les offres ouvertes pour <strong>{{ clientName }}</strong>.
            </p>
          </div>
          <router-link :to="{ name: 'ClientDashboard' }" class="btn btn-outline-light">
            <i class="fas fa-arrow-left me-1"></i> Tableau de bord
          </router-link>
        </div>
      </div>
    </section>

    <div class="container py-5">
      <!-- Loading -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status"></div>
        <p class="mt-3 text-muted">Chargement des offres...</p>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="alert alert-warning">{{ error }}</div>

      <!-- Empty -->
      <div v-else-if="!offers.length" class="empty-state">
        <i class="fas fa-folder-open"></i>
        <h5>Aucune offre en cours</h5>
        <p class="text-muted mb-0">
          Aucune offre de recrutement n'a encore été ouverte pour votre entreprise.
        </p>
      </div>

      <!-- Filters -->
      <div v-else>
        <div class="filter-bar mb-4">
          <div class="input-group input-group-lg">
            <span class="input-group-text bg-white border-end-0">
              <i class="fas fa-search text-muted"></i>
            </span>
            <input
              v-model="searchQuery"
              type="text"
              class="form-control border-start-0"
              placeholder="Rechercher une offre par titre..."
            />
          </div>
        </div>

        <!-- Offers grid -->
        <div class="row g-4">
          <div
            v-for="offer in filteredOffers"
            :key="offer.id"
            class="col-md-6 col-lg-4"
          >
            <div class="offer-card" :class="{ 'offer-inactive': !offer.is_active || offer.is_expired }">
              <div class="offer-status-badges">
                <span v-if="!offer.is_active" class="badge-status badge-closed">
                  Clôturée
                </span>
                <span v-else-if="offer.is_expired" class="badge-status badge-expired">
                  Expirée
                </span>
                <span v-else class="badge-status badge-active">
                  <i class="fas fa-circle me-1"></i>Active
                </span>
              </div>

              <h4 class="offer-title">{{ offer.title }}</h4>
              <p class="offer-description" v-html="truncate(offer.description, 120)"></p>

              <div class="offer-stats">
                <div class="stat-item">
                  <div class="stat-number">{{ offer.applications_count }}</div>
                  <div class="stat-name">Candidatures</div>
                </div>
                <div class="stat-item highlight">
                  <div class="stat-number">{{ offer.preselected_count }}</div>
                  <div class="stat-name">Présélectionnés</div>
                </div>
                <div class="stat-item">
                  <div class="stat-number">{{ offer.selected_count }}</div>
                  <div class="stat-name">Retenus</div>
                </div>
              </div>

              <div class="offer-meta">
                <span v-if="offer.formatted_deadline">
                  <i class="far fa-calendar me-1"></i>
                  {{ offer.formatted_deadline }}
                </span>
              </div>

              <router-link
                :to="{ name: 'ClientOfferDetail', params: { id: offer.id } }"
                class="btn btn-primary w-100 mt-3"
                :class="{ disabled: !offer.preselected_count }"
              >
                <i class="fas fa-users me-1"></i>
                {{ offer.preselected_count ? 'Voir les candidats' : 'Pas encore de présélection' }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import clientApi from '@/services/clientApi'

export default {
  name: 'ClientOffers',
  data() {
    return {
      loading: true,
      error: null,
      offers: [],
      searchQuery: '',
      clientName: localStorage.getItem('client_name') || '',
    }
  },
  computed: {
    filteredOffers() {
      if (!this.searchQuery.trim()) return this.offers
      const q = this.searchQuery.toLowerCase()
      return this.offers.filter((o) => o.title.toLowerCase().includes(q))
    },
  },
  mounted() {
    this.loadOffers()
  },
  methods: {
    async loadOffers() {
      this.loading = true
      this.error = null
      try {
        const { data } = await clientApi.get('/client/job-offers')
        this.offers = (data.data && data.data.offers) || data.offers || []
      } catch (e) {
        this.error = 'Impossible de charger les offres.'
      } finally {
        this.loading = false
      }
    },
    truncate(text, max) {
      if (!text) return ''
      const clean = text.replace(/<[^>]*>/g, '').trim()
      if (clean.length <= max) return clean
      return clean.substring(0, max) + '...'
    },
  },
}
</script>

<style scoped>
.client-offers {
  min-height: 100vh;
  background: #f8f9fa;
}

.page-header {
  background: linear-gradient(135deg, #2596be 0%, #29b3cd 100%);
  color: #fff;
  padding: 3rem 0 3.5rem;
}

.page-title {
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 0.3rem;
}

.page-subtitle {
  opacity: 0.9;
}

.filter-bar .form-control,
.filter-bar .input-group-text {
  border-radius: 50px !important;
  border-color: #e3e8ee;
  padding: 0.8rem 1rem;
}

.filter-bar .input-group-text {
  border-right: none;
  padding-right: 0.5rem;
}

.filter-bar .form-control {
  border-left: none;
  box-shadow: none;
}

.offer-card {
  background: #fff;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.05);
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s, box-shadow 0.2s;
  position: relative;
}

.offer-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(37, 150, 190, 0.15);
}

.offer-inactive {
  opacity: 0.75;
}

.offer-status-badges {
  margin-bottom: 0.8rem;
}

.badge-status {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 50px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.badge-active {
  background: rgba(40, 167, 69, 0.12);
  color: #28a745;
}

.badge-active i {
  font-size: 0.5rem;
  vertical-align: middle;
}

.badge-closed {
  background: rgba(108, 117, 125, 0.12);
  color: #6c757d;
}

.badge-expired {
  background: rgba(220, 53, 69, 0.12);
  color: #dc3545;
}

.offer-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.6rem;
}

.offer-description {
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 1rem;
  flex-grow: 1;
}

.offer-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  padding: 0.8rem 0;
  border-top: 1px solid #f0f2f5;
  border-bottom: 1px solid #f0f2f5;
}

.stat-item {
  text-align: center;
}

.stat-item.highlight .stat-number {
  color: #e95d16;
}

.stat-number {
  font-size: 1.3rem;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1;
}

.stat-name {
  font-size: 0.7rem;
  color: #6c757d;
  margin-top: 0.3rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.offer-meta {
  margin-top: 0.8rem;
  font-size: 0.82rem;
  color: #6c757d;
}

.btn-primary {
  background: linear-gradient(135deg, #2596be, #29b3cd);
  border: none;
  border-radius: 10px;
  padding: 0.7rem;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s;
}

.btn-primary:hover:not(.disabled) {
  background: linear-gradient(135deg, #1e7a9b, #2596be);
  transform: translateY(-1px);
}

.btn-primary.disabled {
  background: #e9ecef;
  color: #6c757d;
  pointer-events: none;
}

.empty-state {
  background: #fff;
  border-radius: 14px;
  padding: 4rem 1.5rem;
  text-align: center;
}

.empty-state i {
  font-size: 3rem;
  color: #dee2e6;
  margin-bottom: 1rem;
}
</style>
